import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, TabStyles, TextAreaWidget } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { style } from 'typestyle';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { IMetricInfo } from '../../../ts/models/IMetricInfo';
import { Metrics } from '../../../ts/resources/Metrics';
import { ISetDescriptionAction } from '../../../ts/ServerApi/types/actionNames';
import { selectAvailableActionsForEntity } from '../../selectors/EntityActionsSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { MarkupField } from '../Components/MarkupField';
import { editBlockStyleClass, editButtonClass, metadataSectionStyle, metadataSubTitle, metadataText, resolveButton, resolveButtonContainer } from './MetricsStyles';

export interface MetricDescriptionSectionOwnProps {
  metricInfo: IMetricInfo;
}

export interface MetricDescriptionSectionAngularProps {
  metricsService: Metrics;
}

export interface MetricDescriptionSectionStateProps {
  canEditDescription: boolean;
}

export type MetricDescriptionSectionNonAngularProps = MetricDescriptionSectionOwnProps & MetricDescriptionSectionStateProps;
export type MetricDescriptionSectionProps = MetricDescriptionSectionNonAngularProps & MetricDescriptionSectionAngularProps;

export interface MetricDescriptionSectionState {
  isEditingDescription: boolean;
  displayedDescription: string;
  savedDescription: string;
  isSaving: boolean;
}

const descriptionMaxLength: number = 255;

export class MetricDescriptionSection extends React.PureComponent<MetricDescriptionSectionProps, MetricDescriptionSectionState> {
  public constructor(props: Readonly<MetricDescriptionSectionProps>) {
    super(props);
    this.state = {
      isEditingDescription: false,
      displayedDescription: props.metricInfo.description,
      savedDescription: props.metricInfo.description,
      isSaving: false
    };
  }

  private handleEditClick = (event: React.MouseEvent) => {
    const isTargetElementAnAnchorTag = (event.target as HTMLElement).tagName.toLowerCase() === 'a';

    // If an anchor tag is clicked do not enter editing mode
    if (isTargetElementAnAnchorTag) return;

    this.setState({ isEditingDescription: true });
  }

  public render(): JSX.Element {
    const canEnterEditModeWithClick = this.props.canEditDescription && !this.state.isEditingDescription;
    const editModeHandler = canEnterEditModeWithClick ? this.handleEditClick : undefined;

    return (
      <div
      className={[metadataSectionStyle, canEnterEditModeWithClick ? editBlockStyleClass : ''].join(' ')}
        onClick={editModeHandler}
        data-tb-test-id={'metadata-description-container'}
      >
        <div style={descriptionTitleStyle}>
          <div style={metadataSubTitle} id="description-label">{Messages.metricsMetadataPanelDescription()}</div>
          {canEnterEditModeWithClick ?
            <button className={editButtonClass} aria-label={Messages.metricsMetadataPanelEditDescriptionLabel()} onClick={editModeHandler} />
            : null
          }
        </div>
        {this.renderDescription()}
      </div>
    );
  }

  private renderDescription = () => {
    if (this.state.isEditingDescription) {
      return (
        <div style={metadataText} aria-labeled-by="description-label">
          <TextAreaWidget
            label=""
            testId="description-editor"
            value={this.state.displayedDescription}
            onChange={e => this.descriptionChangeCallback(e.target.value)}
            message={Messages.metricsMetadataPanelDescriptionCharactersRemaining({
              characters: (descriptionMaxLength - this.state.displayedDescription.length).toString()
            })}
            maxLength={descriptionMaxLength}
            rows={3}
            style={descriptionTextArea}
            className={style({ width: '100%' })}
            disabled={this.state.isSaving}
          />
          <div style={{...resolveButtonContainer, marginTop: '3px'}}>
            <ButtonWidget
              style={resolveButton}
              buttonType={ButtonType.Outline}
              onClick={this.handleCancelClick}
              testId="cancel-description-edit"
              disabled={this.state.isSaving}
            >
              {Messages.cancel()}
            </ButtonWidget>
            <ButtonWidget
              style={resolveButton}
              buttonType={ButtonType.Primary}
              onClick={this.handleSaveClick}
              testId="save-description"
            >
            {
              this.state.isSaving ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget onDarkBackground spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                </div> :
                <span>{Messages.save()}</span>
            }
            </ButtonWidget>
          </div>
        </div>
      );
    } else {
      const descriptionToDisplay = this.state.displayedDescription || Messages.metricsMetadataPanelNoDescription();
      return (
        <div style={metadataText} aria-labelledby="description-label">
          <MarkupField testId={'metadata-description'} markup={descriptionToDisplay} />
        </div>
      );
    }
  }

  private descriptionChangeCallback = (userInput: string) => {
    this.setState({ displayedDescription: userInput });
  }

  private handleSaveClick = () => {
    if (this.state.isSaving) return;

    this.setState({
      isSaving: true,
    }, this.saveDescriptionAndStopEditing);
  }

  private saveDescriptionAndStopEditing = async () => {
    try {
      await this.props.metricsService.setMetricDescription({
        description: this.state.displayedDescription,
        id: this.props.metricInfo.id,
      });
      this.setState({
        isEditingDescription: false,
        savedDescription: this.state.displayedDescription,
        isSaving: false
      });
    }
    catch {
      this.setState({
        isEditingDescription: true,
        isSaving: false
      });
    }
  }

  private handleCancelClick = () => {
    this.setState({
      isEditingDescription: false,
      displayedDescription: this.state.savedDescription,
    });
  }
}

export namespace MetricDescriptionSectionAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const Metrics = ServiceLocator.get<Metrics>('Metrics');

    return {
      metricsService: Metrics,
    };
  }

  function selectPropsFromAngularServices(angularServices: MetricDescriptionSectionAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MetricDescriptionSectionNonAngularProps, MetricDescriptionSectionAngularProps, MetricDescriptionSectionAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const MetricDescriptionSectionAngular: React.ComponentType<MetricDescriptionSectionOwnProps> =
  MetricDescriptionSectionAngularProvider.HOC(MetricDescriptionSection);

const mapStateToProps: MapStateToProps<MetricDescriptionSectionStateProps, MetricDescriptionSectionOwnProps> = (state, ownProps) => {
  const availableActions = selectAvailableActionsForEntity(state, ownProps.metricInfo);
  return {
    canEditDescription: availableActions && (availableActions as ISetDescriptionAction).setDescription
  };
};

export const ConnectedMetricDescriptionSection: React.ComponentClass<MetricDescriptionSectionOwnProps>
  = connect(mapStateToProps, undefined)(MetricDescriptionSectionAngular);

const descriptionTextArea: React.CSSProperties = {
  width: '100%',
  resize: 'none',
  display: 'block',
};

const descriptionTitleStyle = {
  display: 'flex',
};
